/**
 * BrightPath - BudgetProgressBar Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import type { BudgetStatus } from '../types';
import { EXPENSE_CATEGORIES } from '../types';
import { formatCurrency, cn } from '@/core/lib/utils';

interface BudgetProgressBarProps {
  budgetStatus: BudgetStatus;
  showDetails?: boolean;
  compact?: boolean;
  onClick?: () => void;
}

export function BudgetProgressBar({
  budgetStatus,
  showDetails = true,
  compact = false,
  onClick,
}: BudgetProgressBarProps) {
  const { budget, spent, remaining, percentageUsed, isOverBudget, isNearThreshold, projectedMonthEnd, daysRemaining } = budgetStatus;
  const categoryConfig = EXPENSE_CATEGORIES[budget.category];

  const getProgressColor = () => {
    if (isOverBudget) return 'bg-red-500';
    if (isNearThreshold) return 'bg-amber-500';
    if (percentageUsed > 50) return 'bg-terracotta';
    return 'bg-sage';
  };

  const getStatusIcon = () => {
    if (isOverBudget) return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (isNearThreshold) return <TrendingUp className="w-4 h-4 text-amber-500" />;
    return <CheckCircle className="w-4 h-4 text-sage" />;
  };

  if (compact) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'w-full text-left p-3 rounded-lg border transition-all',
          onClick ? 'hover:shadow-md cursor-pointer' : '',
          isOverBudget
            ? 'border-red-200 bg-red-50'
            : isNearThreshold
            ? 'border-amber-200 bg-amber-50'
            : 'border-warm-gray/10 bg-white'
        )}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-navy text-sm">{categoryConfig.label}</span>
          {getStatusIcon()}
        </div>
        <div className="h-2 bg-cream-dark rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-500', getProgressColor())}
            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-warm-gray">
          <span>{formatCurrency(spent)}</span>
          <span>{formatCurrency(budget.monthlyBudget)}</span>
        </div>
      </button>
    );
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        'p-4 rounded-xl border transition-all',
        onClick ? 'hover:shadow-md cursor-pointer' : '',
        isOverBudget
          ? 'border-red-200 bg-gradient-to-r from-red-50 to-white'
          : isNearThreshold
          ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-white'
          : 'border-warm-gray/10 bg-white'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
          >
            {getStatusIcon()}
          </div>
          <div>
            <h4 className="font-semibold text-navy">{categoryConfig.label}</h4>
            <p className="text-sm text-warm-gray">
              {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-navy">{percentageUsed.toFixed(0)}%</p>
          <p className="text-xs text-warm-gray">of budget</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-3 bg-cream-dark rounded-full overflow-hidden mb-3">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-500',
            getProgressColor()
          )}
          style={{ width: `${Math.min(percentageUsed, 100)}%` }}
        />
        {/* Threshold marker */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-navy/30"
          style={{ left: `${budget.alertThreshold}%` }}
        />
        {/* Over budget indicator */}
        {isOverBudget && (
          <div
            className="absolute inset-y-0 right-0 bg-red-500/20 rounded-r-full"
            style={{ width: `${percentageUsed - 100}%` }}
          />
        )}
      </div>

      {/* Details */}
      {showDetails && (
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-lg font-semibold text-navy">{formatCurrency(spent)}</p>
            <p className="text-xs text-warm-gray">Spent</p>
          </div>
          <div>
            <p
              className={cn(
                'text-lg font-semibold',
                remaining < 0 ? 'text-red-500' : 'text-sage'
              )}
            >
              {formatCurrency(Math.abs(remaining))}
            </p>
            <p className="text-xs text-warm-gray">{remaining < 0 ? 'Over' : 'Remaining'}</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-navy">
              {formatCurrency(budget.monthlyBudget)}
            </p>
            <p className="text-xs text-warm-gray">Budget</p>
          </div>
        </div>
      )}

      {/* Projection Warning */}
      {showDetails && projectedMonthEnd > budget.monthlyBudget && !isOverBudget && (
        <div className="mt-3 pt-3 border-t border-warm-gray/10 flex items-center gap-2 text-sm text-amber-600">
          <TrendingUp className="w-4 h-4" />
          <span>
            Projected: {formatCurrency(projectedMonthEnd)} by month end
          </span>
        </div>
      )}
    </div>
  );
}

export default BudgetProgressBar;
